import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { motion } from "framer-motion";
import { Star, X } from "lucide-react";

import { RegistrationFields } from "./registration/RegistrationFields";
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = "https://qoetrgckdwjkezgiaxsh.supabase.co";
const supabaseKey = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InFvZXRyZ2NrZHdqa2V6Z2lheHNoIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzU4MDM5NDEsImV4cCI6MjA1MTM3OTk0MX0.ToDuaWRV-eqUiVvkIq9paY5Z8fwOw2yamgja4esD2O0";
const supabase = createClient(supabaseUrl, supabaseKey);

interface RegistrationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const RegistrationModal = ({ open, onOpenChange }: RegistrationModalProps) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    parentName: "",
    parentMobile: "",
    email: "",
    childName: "",
    childAge: "",
    city: "",
    timestamp: ""
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const now = new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
      const timestamp = now;
  
      const dataWithTimestamp = {
        ...formData,
        timestamp
      };

      const { error } = await supabase
        .from('registrations')
        .insert([
          {
            parent_name: dataWithTimestamp.parentName,
            parent_mobile: dataWithTimestamp.parentMobile,
            email: dataWithTimestamp.email,
            child_name: dataWithTimestamp.childName,
            child_age: dataWithTimestamp.childAge,
            city: dataWithTimestamp.city,
            timestamp: dataWithTimestamp.timestamp
          }
        ]);

      if (error) throw error;

      toast({
        title: "🎉 Registration Successful!",
        description: "We'll contact you shortly to start your child's amazing journey!",
      });
      onOpenChange(false);
      setFormData({
        parentName: "",
        parentMobile: "",
        email: "",
        childName: "",
        childAge: "",
        city: "",
        timestamp: ""
      });
    } catch (error) {
      toast({
        title: "Registration Failed",
        description: "Please try again or contact support.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] w-[90vw] max-w-[600px] max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-xl p-0 border-0 md:w-full">
        <div className="sticky top-0 z-50 bg-white rounded-t-2xl">
          <div className="absolute right-4 top-4">
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full hover:bg-gray-100"
              onClick={() => onOpenChange(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          <div className="bg-gradient-to-r from-primary via-secondary to-accent p-6">
            <DialogHeader>
              <DialogTitle className="text-2xl md:text-3xl font-bold text-white text-center">
                🌟Reserve Your Spot for a Happier, Stronger Future for Your Child! 🌟
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-base md:text-xl font-normal text-white/90 mt-2"
                >

                </motion.div>
              </DialogTitle>
            </DialogHeader>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-6 bg-white">
          <RegistrationFields 
            formData={formData}
            handleChange={handleChange}
            setFormData={setFormData}
          />

          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-6 rounded-xl relative overflow-hidden group"
            >
              <span className="relative z-10 flex items-center justify-center gap-2 text-base md:text-lg">
                {isSubmitting ? "Submitting..." : "Start Your Journey Now!"}
                <Star className="w-5 h-5 group-hover:rotate-45 transition-transform" />
              </span>
            </Button>
          </motion.div>

          <p className="text-center text-xs md:text-sm text-gray-500">
            Limited spots available! Join 1000+ happy kids in our centers.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};
